import {Injectable, OnDestroy} from '@angular/core';
import {Capacitor, PluginListenerHandle} from '@capacitor/core';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {MobileExperienceConfigService} from './mobile-experience-config.service';
import {
  FlutterShell,
  FlutterShellDismissEvent,
  FlutterMediaComposerPresentOptions,
  FlutterMediaComposerResult,
  FlutterSocialListOutcome,
  FlutterSocialListPresentOptions,
  FlutterVehicleWizardResult,
} from './flutter-shell.plugin';
import {environment} from '../../../environments/environment';
import {appChannelCode} from '../feature-flags/app-channel';

/**
 * Opens only native Flutter flows over the live Capacitor WebView. It never
 * owns application navigation or replaces Angular's root screen.
 */
@Injectable({providedIn: 'root'})
export class FlutterShellService implements OnDestroy {
  private listenersReady: Promise<void> | null = null;
  private didDismissListener?: PluginListenerHandle;
  private presenting = false;
  private completion?: (event: FlutterShellDismissEvent) => void;

  constructor(
    private readonly mobileConfig: MobileExperienceConfigService,
    private readonly translate: TranslateService,
    private readonly router: Router,
  ) {}

  public async presentVehicleWizard(): Promise<FlutterVehicleWizardResult | null> {
    if (!this.isVehicleWizardEligible() || this.presenting) {
      return null;
    }

    try {
      await this.ensureListeners();
      this.presenting = true;
      const completed = new Promise<FlutterShellDismissEvent>((resolve) => {
        this.completion = resolve;
      });
      await FlutterShell.present({
        apiUrl: environment.apiUrl,
        locale: this.translate.currentLang || this.translate.defaultLang || 'pl-PL',
        channelCode: appChannelCode(),
        flow: 'vehicle-wizard',
      });
      const event = await completed;
      if (event.outcome === 'created') {
        return {
          outcome: 'created',
          projectJson: event.projectJson,
          vehicleIri: event.vehicleIri,
          vehicleIdentity: event.vehicleIdentity,
          vehicleTypeCode: event.vehicleTypeCode,
        };
      }
      if (event.outcome === 'cancelled') {
        return {outcome: 'cancelled'};
      }
      return null;
    } catch {
      this.presenting = false;
      this.completion = undefined;
      return null;
    }
  }

  public get canPresentSocialList(): boolean {
    return Capacitor.isNativePlatform()
      && this.mobileConfig.current.mobileShellEnabled
      && this.mobileConfig.current.mobileFlutterSocialListEnabled
      && !this.presenting;
  }

  public get canPresentMediaComposer(): boolean {
    return Capacitor.isNativePlatform()
      && this.mobileConfig.current.mobileShellEnabled
      && this.mobileConfig.current.mobileFlutterMediaComposerEnabled
      && !this.presenting;
  }

  public async presentMediaComposer(start: FlutterMediaComposerPresentOptions['start']): Promise<FlutterMediaComposerResult | null> {
    if (!this.canPresentMediaComposer) {
      return null;
    }

    try {
      await this.ensureListeners();
      this.presenting = true;
      const completed = new Promise<FlutterShellDismissEvent>((resolve) => {
        this.completion = resolve;
      });
      await FlutterShell.present({
        apiUrl: environment.apiUrl,
        locale: this.translate.currentLang || this.translate.defaultLang || 'pl-PL',
        channelCode: appChannelCode(),
        flow: 'media-composer',
        start,
      });
      const event = await completed;
      if (event.outcome !== 'completed' || !event.mediaPath || !event.mediaType || !event.mimeType) {
        return null;
      }
      return {
        outcome: 'completed',
        mediaPath: event.mediaPath,
        mediaUrl: event.mediaUrl,
        mediaType: event.mediaType,
        mimeType: event.mimeType,
        thumbnailPath: event.thumbnailPath,
        thumbnailUrl: event.thumbnailUrl,
        thumbnailTime: event.thumbnailTime,
        filterPreset: event.filterPreset,
        caption: event.caption,
      };
    } catch {
      this.presenting = false;
      this.completion = undefined;
      return null;
    }
  }

  public async presentSocialList(options: Omit<FlutterSocialListPresentOptions, 'apiUrl' | 'locale' | 'channelCode' | 'flow'>): Promise<FlutterSocialListOutcome | null> {
    if (!this.canPresentSocialList) {
      return null;
    }

    try {
      await this.ensureListeners();
      this.presenting = true;
      const completed = new Promise<FlutterShellDismissEvent>((resolve) => {
        this.completion = resolve;
      });
      await FlutterShell.present({
        ...options,
        apiUrl: environment.apiUrl,
        locale: this.translate.currentLang || this.translate.defaultLang || 'pl-PL',
        channelCode: appChannelCode(),
        flow: 'social-list',
      });
      const event = await completed;
      if (event.route) {
        await this.router.navigateByUrl(event.route);
      }
      return event.outcome === 'navigated' ? 'navigated' : 'closed';
    } catch {
      this.presenting = false;
      this.completion = undefined;
      return null;
    }
  }

  public async dismiss(): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.presenting) {
      return;
    }
    try {
      await FlutterShell.dismiss();
    } catch {
      // The native bridge may be absent in a development browser build.
    }
  }

  public get isPresenting(): boolean {
    return this.presenting;
  }

  public ngOnDestroy(): void {
    void this.didDismissListener?.remove();
    this.didDismissListener = undefined;
    this.completion = undefined;
  }

  private isVehicleWizardEligible(): boolean {
    return Capacitor.isNativePlatform()
      && this.mobileConfig.current.mobileFlutterVehicleWizardEnabled;
  }

  private async ensureListeners(): Promise<void> {
    if (!this.listenersReady) {
      this.listenersReady = FlutterShell.addListener('didDismiss', (event) => {
        const completion = this.completion;
        this.completion = undefined;
        this.presenting = false;
        completion?.(event);
      }).then((listener) => {
        this.didDismissListener = listener;
      });
    }
    await this.listenersReady;
  }
}
